export type BookingStatus = 'pending' | 'confirmed' | 'cancelled';

export type SportType = 'futsal' | 'indoor_cricket' | 'indoor_cricket_nets' | 'badminton' | 'pickleball' | 'indoor_golf';

export type PaymentGateway = 'esewa' | 'khalti' | 'connectips' | 'fonepay' | 'bank' | 'cash' | 'qr';

export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export type PaymentSource = 'online' | 'manual';

export type BookingPaymentStatus =
  | 'unpaid'
  | 'pending'
  | 'partially_paid'
  | 'paid'
  | 'failed'
  | 'refunded';

export interface PaymentSummary {
  paymentStatus: BookingPaymentStatus;
  paidAmount: number;
  remainingAmount: number;
  latestPayment: Payment | null;
  canPay: boolean;
  canRecordManualPayment: boolean;
}

export interface Booking {
  id: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  sport: SportType;
  date: string;
  slot: string;
  amount: number;
  membershipDiscountPercent?: number;
  membershipId?: string | null;
  status: BookingStatus;
  notes?: string;
  cancelledAt?: string | null;
  createdAt: string;
  cancelEligibility?: {
    canCancel: boolean;
    reason?: string;
  };
  paymentSummary?: PaymentSummary;
  paymentAccessToken?: string;
}

export type ScheduleSlotState = 'available' | 'booked' | 'closed' | 'past';
export type ScheduleView = 'day' | 'week';

export interface ScheduleSlotBooking {
  id: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  status: Extract<BookingStatus, 'pending' | 'confirmed'>;
  amount: number;
  paymentStatus?: BookingPaymentStatus;
}

export interface ScheduleSlot {
  slot: string;
  state: ScheduleSlotState;
  booking: ScheduleSlotBooking | null;
}

export interface ScheduleCourtDay {
  sport: SportType;
  date: string;
  closed: boolean;
  enabled: boolean;
  slots: ScheduleSlot[];
}

export interface ScheduleGrid {
  from: string;
  to: string;
  dates: string[];
  sports: SportType[];
  days: ScheduleCourtDay[];
}

export interface Payment {
  id: string;
  bookingId: string;
  gateway: PaymentGateway;
  amount: number;
  status: PaymentStatus;
  source: PaymentSource;
  reference?: string;
  notes?: string;
  attachmentFileIds: string[];
  paidAt: string | null;
  recordedBy: string | null;
  transactionUuid?: string;
  createdAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
}

export interface CustomerUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
}

export interface AdminCustomerUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  emailVerified: boolean;
  createdAt: string;
  activeMembership: UserMembership | null;
}

export interface AdminUserMembershipRow extends UserMembership {
  payments: {
    id: string;
    gateway: PaymentGateway;
    amount: number;
    status: PaymentStatus;
    source?: PaymentSource;
    reference?: string;
    notes?: string;
    paidAt: string | null;
    createdAt: string;
  }[];
}

export interface AdminCustomerDetail {
  user: AdminCustomerUser;
  memberships: AdminUserMembershipRow[];
  pendingMemberships: AdminUserMembershipRow[];
}

export interface CourtSettings {
  id: string;
  sport: SportType;
  slotDurationMinutes: number;
  pricePerSlot: number;
  openTime: string;
  closeTime: string;
  openDays: number[];
  enabled: boolean;
  discountPercent: number;
  discountValidTill: string | null;
  cancellationAllowed: boolean;
  cancellationCutoffMinutes: number | null;
  effectivePricePerSlot: number;
  slots: string[];
  updatedAt: string;
}

export interface CourtSettingsInput {
  slotDurationMinutes: number;
  pricePerSlot: number;
  openTime: string;
  closeTime: string;
  openDays: number[];
  enabled: boolean;
  discountPercent: number;
  discountValidTill: string | null;
  cancellationAllowed: boolean;
  cancellationCutoffMinutes: number | null;
}

export interface MembershipPlan {
  id: string;
  name: string;
  durationMonths: number;
  price: number;
  bookingDiscountPercent: number;
  description: string;
  benefits: string[];
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
}

export interface MembershipPlanInput {
  name: string;
  durationMonths: number;
  price: number;
  bookingDiscountPercent: number;
  description: string;
  benefits: string[];
  enabled: boolean;
  sortOrder: number;
}

export type UserMembershipStatus = 'pending' | 'active' | 'expired' | 'cancelled';

export type MembershipPaymentStatus = 'unpaid' | 'paid' | 'failed' | 'refunded';

export interface MembershipPaymentSummary {
  paymentStatus: MembershipPaymentStatus;
  latestPayment: {
    id: string;
    membershipId: string;
    gateway: PaymentGateway;
    amount: number;
    status: PaymentStatus;
    paidAt: string | null;
    transactionUuid?: string;
    createdAt: string;
  } | null;
  canPay: boolean;
}

export interface UserMembership {
  id: string;
  planId: string;
  planName: string;
  durationMonths: number;
  planPrice: number;
  bookingDiscountPercent: number;
  startDate: string;
  expiresAt: string;
  status: UserMembershipStatus;
  isValid: boolean;
  createdAt: string;
  paymentSummary?: MembershipPaymentSummary;
}

export interface BookingPricing {
  basePrice: number;
  courtEffectivePrice: number;
  courtDiscountPercent: number;
  membershipDiscountPercent: number;
  finalAmount: number;
  membershipId: string | null;
  hasActiveMembership: boolean;
}

export type AdminNotificationType =
  | 'booking_created'
  | 'booking_confirmed'
  | 'booking_cancelled'
  | 'payment_received';

export interface AdminNotification {
  id: string;
  type: AdminNotificationType;
  title: string;
  message: string;
  link: string;
  bookingId?: string;
  paymentId?: string;
  read: boolean;
  createdAt: string;
}

export interface DashboardAnalytics {
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  totalRevenue: number;
  totalDueAmount: number;
  bookingsBySport: { sport: SportType; count: number }[];
  revenueByMonth: { month: string; revenue: number }[];
  paymentsByGateway: { gateway: PaymentGateway; count: number }[];
}

export const DAY_LABELS: { value: number; label: string }[] = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export interface VenueShowcaseItem {
  id: string;
  src: string;
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId: string | null;
  staticSrc: string | null;
  updatedAt: string;
}

export type GalleryEntryType = 'image' | 'highlight';
export type GalleryHighlightIcon = 'lighting' | 'surface' | 'climate' | 'sports';

export interface GalleryImageItem {
  id: string;
  entryType: 'image';
  src: string;
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId: string | null;
  staticSrc: string | null;
  updatedAt: string;
}

export interface GalleryHighlightItem {
  id: string;
  entryType: 'highlight';
  title: string;
  description: string;
  iconKey: GalleryHighlightIcon;
  accentColor: string | null;
  sortOrder: number;
  enabled: boolean;
  updatedAt: string;
}

export type GalleryEntry = GalleryImageItem | GalleryHighlightItem;

export interface GalleryImageInput {
  entryType: 'image';
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId?: string | null;
  staticSrc?: string | null;
}

export interface GalleryHighlightInput {
  entryType: 'highlight';
  title: string;
  description: string;
  iconKey: GalleryHighlightIcon;
  accentColor?: string;
  sortOrder: number;
  enabled: boolean;
}

export type GalleryEntryInput = GalleryImageInput | GalleryHighlightInput;

export interface VenueShowcaseInput {
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId?: string | null;
  staticSrc?: string | null;
}

export const SPORT_LABELS: Record<SportType, string> = {
  futsal: 'Futsal',
  indoor_cricket: 'Indoor Cricket',
  indoor_cricket_nets: 'Indoor Cricket Nets',
  badminton: 'Badminton',
  pickleball: 'Pickle Ball',
  indoor_golf: 'Indoor Golf',
};
